// =============================================================================
// energy-service.ts — pure-function port of Unity EnergyService.cs
// No class, no singleton, no module-level state.
// =============================================================================

import type { ActionLog } from "../rng.js";
import { assertFiniteInt } from "./_validation.js";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

export const DEFICIT_POLLUTION_MULTIPLIER = 5;

// ---------------------------------------------------------------------------
// PlayerEnergyState  (M: frozen plain object instead of mutable Map)
// ---------------------------------------------------------------------------

export interface PlayerEnergyState {
  /** seat index → energy cube count. Missing key treated as 0. */
  readonly energy: Readonly<Record<string, number>>;
}

// ---------------------------------------------------------------------------
// Result types
// ---------------------------------------------------------------------------

export interface EnergyTrySpendResult {
  readonly ok: boolean;
  readonly newState: PlayerEnergyState;
  readonly log: ActionLog;
  /** Energy before the spend attempt. */
  readonly prev: number;
  /** Energy after spend (equals prev when ok=false). */
  readonly next: number;
}

export interface EnergyAddResult {
  readonly newState: PlayerEnergyState;
  readonly log: ActionLog;
}

export interface ResolveDeficitResult {
  /** Amount of pollution to add (caller passes this to addPollution). */
  readonly pollutionDelta: number;
  readonly log: ActionLog;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getEnergy(state: PlayerEnergyState, seat: number): number {
  return state.energy[seat] ?? 0;
}

function setEnergy(state: PlayerEnergyState, seat: number, value: number): PlayerEnergyState {
  const next = Object.freeze({ ...state.energy, [seat]: value });
  return Object.freeze({ energy: next });
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Returns current energy for a seat (0 if not set).
 */
export function getEnergyCount(state: PlayerEnergyState, seat: number): number {
  return getEnergy(state, seat);
}

/**
 * Attempt to spend `amount` energy cubes from `seat`.
 * Returns ok=false (no state change) on shortfall or negative amount.
 * Negative amounts are logged via console.error.
 * prev/next always populated on result. (H parity)
 */
export function trySpendEnergy(
  state: PlayerEnergyState,
  log: ActionLog,
  seat: number,
  amount: number,
  source: string,
): EnergyTrySpendResult {
  // L: input validation
  assertFiniteInt(seat, "trySpendEnergy seat");
  assertFiniteInt(amount, "trySpendEnergy amount");

  if (amount < 0) {
    console.error(`[EnergyService] negative spend amount ${amount} seat=${seat} src=${source}`);
    const prev = getEnergy(state, seat);
    return { ok: false, newState: state, log, prev, next: prev };
  }
  const prev = getEnergy(state, seat);
  if (prev < amount) {
    const nextLog = log.append({
      kind: "energy.spend.insufficient",
      data: { seat, amount, source, energyBefore: prev },
    });
    return { ok: false, newState: state, log: nextLog, prev, next: prev };
  }
  const next = prev - amount;
  const newState = setEnergy(state, seat, next);
  const nextLog = log.append({
    kind: "energy.spend",
    data: { seat, amount, source, energyBefore: prev, energyAfter: next },
  });
  return {
    ok: true,
    newState: Object.freeze(newState),
    log: nextLog,
    prev,
    next,
  };
}

/**
 * Add `delta` energy cubes to `seat`. Clamps to 0 (matches Unity Mathf.Max(0, prev+delta)).
 */
export function addEnergy(
  state: PlayerEnergyState,
  log: ActionLog,
  seat: number,
  delta: number,
  source: string,
): EnergyAddResult {
  // L: input validation
  assertFiniteInt(seat, "addEnergy seat");
  assertFiniteInt(delta, "addEnergy delta");

  const prev = getEnergy(state, seat);
  const next = Math.max(0, prev + delta);
  const newState = setEnergy(state, seat, next);
  const nextLog = log.append({
    kind: "energy.add",
    data: { seat, delta, source, energyBefore: prev, energyAfter: next },
  });
  return { newState: Object.freeze(newState), log: nextLog };
}

/**
 * Convert an energy shortfall (deficit > 0) into pollution delta at 5× rate.
 * Caller passes the deficit (positive int) — this function does NOT deduct energy.
 * Returns pollutionDelta for the caller to apply via addPollution.
 */
export function resolveDeficit(
  log: ActionLog,
  seat: number,
  deficit: number,
): ResolveDeficitResult {
  if (deficit <= 0) {
    return { pollutionDelta: 0, log };
  }
  const pollutionDelta = deficit * DEFICIT_POLLUTION_MULTIPLIER;
  const nextLog = log.append({
    kind: "energy.deficit.resolved",
    data: { seat, deficit, pollutionDelta },
  });
  return { pollutionDelta, log: nextLog };
}

/**
 * Create initial empty energy state.
 */
export function createEnergyState(): PlayerEnergyState {
  return Object.freeze({ energy: Object.freeze({}) });
}

/**
 * Set a seat energy count directly (for test setup / game init).
 */
export function setEnergyCubes(
  state: PlayerEnergyState,
  seat: number,
  value: number,
): PlayerEnergyState {
  // L: input validation
  assertFiniteInt(seat, "setEnergyCubes seat");
  assertFiniteInt(value, "setEnergyCubes value");
  return Object.freeze(setEnergy(state, seat, value));
}
